import { useState, useEffect, useCallback } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Fab,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Mic,
  DragIndicator,
  Call,
  Minimize,
  Pause,
  PhoneForwarded,
  MicOff,
  Search,
  ArrowBack,
  CallEnd,
  Dialpad,
} from '@mui/icons-material';

import { ClientResponseDto } from '@bandido/shared/dtos';

import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { client } from '@frontend/features/common/services';

import CallDuration from './CallDuration';
import { DtmfDialer } from '../DtmfDialer';
import { router } from '@frontend/router/router';
import { routes } from '@frontend/router/routes';
import { AgentStatus } from '@bandido/shared/constants';

interface TransferButtonProps {
  onClick?: () => void;
  isInactive?: boolean;
}
const TransferButton = (props: TransferButtonProps) => {
  const theme = useTheme();

  const sx = {
    border: props.isInactive ? 0 : `1px solid ${theme.palette.secondary.main}`,
    width: 56,
    height: 56,
    boxShadow: theme.shadows[5],
    color: (theme: Theme) => theme.palette.secondary.main,
    backgroundColor: props.isInactive ? theme.palette.secondary.states?.focusVisible : undefined,
    '&:hover': {
      borderColor: props.isInactive ? 'transparent' : theme.palette.secondary.states?.focusVisible,
      backgroundColor: props.isInactive ? theme.palette.secondary.states?.focusVisible : undefined,
    },
  };

  return (
    <IconButton onClick={props.onClick} sx={sx}>
      <PhoneForwarded />
    </IconButton>
  );
};

interface JoiningAgent {
  callSid: string;
  callId: string;
  calleeInfo: ClientResponseDto;
}

type FlexDirection = 'row' | 'column';

const FLOATING_CALL_WINDOW_ATTRS = {
  fullMode: { WIDTH: 360, HEIGHT: 400, DIRECTION: 'column', AVATAR_SIZE: 80 },
  minimizedMode: { WIDTH: 590, HEIGHT: 148, DIRECTION: 'row', AVATAR_SIZE: 52 },
};

export const FloatingCallWindow = () => {
  const { t } = useTranslation();
  const { connection, connectedAgents, callsSocketRef, callData, closeInProgressCall } = useTwilio();
  const { myProfile } = useAuth();

  const [showTransferAgentsList, setShowTransferAgentsList] = useState(false);
  const [joiningAgent, setJoiningAgent] = useState<JoiningAgent>();
  const [hasAgentJoined, setHasAgentJoined] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [fullMode, setFullMode] = useState(true);
  const [search, setSearch] = useState('');
  const [showDtmfDialer, setShowDtmfDialer] = useState(false);

  const handleToggleMode = () => setFullMode((prev) => !prev);

  const handleToggleDtmfDialer = () => {
    if (showTransferAgentsList) setShowTransferAgentsList(false);
    setShowDtmfDialer((prev) => !prev);
  };

  const handleMute = () => {
    connection?.mute(!isMuted);
  };

  const handleTransferMode = (isOpen: boolean) => () => {
    if (showDtmfDialer) setShowDtmfDialer(false);
    setShowTransferAgentsList(isOpen);
  };

  const handleAcceptCallTransfer = () => {
    const { callId } = callData || {};
    if (!callId) return;

    client
      .post(`calls/${callId}/accept-transfer`, {
        agencyId: callData?.agencyId,
      })
      .then(() => {
        setShowTransferAgentsList(false);
        setJoiningAgent(undefined);
        setHasAgentJoined(false);
      });
  };

  const handleHangUp = () => {
    const { callId } = callData || {};
    if (!callId) return;

    client.post(`calls/${callId}/hangup`).then(() => {
      setShowTransferAgentsList(false);
      setJoiningAgent(undefined);
      setHasAgentJoined(false);
      closeInProgressCall();
    });
  };

  const handleTransfer = (agentId: string) => () => {
    const { callId } = callData || {};
    if (!callId) return;

    client
      .post(`calls/${callId}/agent/${agentId}/join`, {
        agencyId: callData?.agencyId,
      })
      .then(() => {
        setShowTransferAgentsList(false);
      });
  };

  const handleCancelTransfer = () => {
    const { callId } = callData || {};
    if (!callId || !joiningAgent?.callSid) return;

    client
      .post(`calls/${callId}/cancel-transfer`, {
        agentCallSid: joiningAgent.callSid,
        agentId: joiningAgent.calleeInfo?.id,
      })
      .then(() => {
        setShowTransferAgentsList(false);
        setJoiningAgent(undefined);
        setHasAgentJoined(false);
      });
  };

  const handleLeaveCall = () => {
    const { callId, calledAgentId } = callData || {};
    if (!callId) return;

    const agentId = joiningAgent?.calleeInfo?.id ?? calledAgentId;

    client.post(`calls/${callId}/agent/${agentId}/leave`).then(() => {
      setShowTransferAgentsList(false);
      setJoiningAgent(undefined);
      setHasAgentJoined(false);
    });
  };

  const handleMuteChange = useCallback((muted: boolean) => {
    setIsMuted(muted);
  }, []);

  const handleNavigateToCallCase = (caseId: number) => () => {
    router.navigate(routes.dashboard.caseView.replace(':id', String(caseId)));
  };

  useEffect(() => {
    connection?.on('mute', handleMuteChange);

    return () => {
      connection?.off('mute', handleMuteChange);
    };
  }, [connection, handleMuteChange]);

  useEffect(() => {
    if (!callsSocketRef || !myProfile?.id) return;

    callsSocketRef.on('call-transfer:requested', (data: string) => {
      const parsedData = JSON.parse(data);
      if (parsedData.agentId === myProfile.id) {
        setJoiningAgent(parsedData);
      }
    });

    callsSocketRef.on('call-transfer:joined', () => {
      if (joiningAgent) setHasAgentJoined(true);
    });

    callsSocketRef.on('call:left', () => {
      setJoiningAgent(undefined);
      setHasAgentJoined(false);
    });

    return () => {
      callsSocketRef.off('call-transfer:requested');
      callsSocketRef.off('call-transfer:joined');
      callsSocketRef.off('call:left');
    };
  }, [callsSocketRef, myProfile?.id, joiningAgent]);

  const currentMode = fullMode ? 'fullMode' : 'minimizedMode';

  const getClientName = (calleeInfo: ClientResponseDto) =>
    calleeInfo ? `${calleeInfo.firstName ?? ''} ${calleeInfo.lastName ?? ''}` : (t('calls.incoming.unknown') as string);

  const { WIDTH, HEIGHT, DIRECTION, AVATAR_SIZE } = FLOATING_CALL_WINDOW_ATTRS[currentMode];

  return (
    <Draggable cancel=".no-drag">
      <Box
        sx={{
          position: 'fixed',
          bottom: `calc(50% - ${WIDTH}px / 2)`,
          right: `calc(50% - ${HEIGHT}px / 2)`,
          borderRadius: 2,
          backgroundColor: (theme) => theme.palette.grey[900],
          color: (theme) => theme.palette.common.white,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          cursor: 'move',
        }}
      >
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={1} alignItems="center">
            <DragIndicator />
            <Call
              sx={{
                color: (theme) => theme.palette.success.main,
              }}
            />
            <Typography variant="subtitle1">{t('calls.inProgress.title')}</Typography>
          </Box>
          <IconButton onClick={handleToggleMode} sx={{ color: (theme) => theme.palette.secondary.main }}>
            <Minimize />
          </IconButton>
        </Box>
        <Divider
          sx={{
            backgroundColor: (theme) => theme.palette.grey[700],
          }}
        />
        <Stack padding={3} direction="row">
          <Stack
            width={WIDTH}
            display="flex"
            alignItems="center"
            padding={3}
            borderRadius={1}
            sx={{
              backgroundColor: (theme) => (joiningAgent ? theme.palette.grey[800] : 'transparent'),
            }}
            flexDirection={DIRECTION as FlexDirection}
            justifyContent={fullMode ? 'center' : 'space-between'}
            gap={4}
          >
            <Stack
              sx={{ opacity: joiningAgent ? 0.5 : 1 }}
              gap={2}
              flexDirection={DIRECTION as FlexDirection}
              alignItems="center"
              justifyContent="center"
            >
              <Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, margin: '0 auto' }} />
              <Stack>
                <Typography color="primary.contrastText" variant="h6" align="center">
                  {getClientName(callData?.calleeInfo as ClientResponseDto)}
                </Typography>
                <Typography color="grey.400" variant="body2" align={fullMode ? 'center' : 'left'}>
                  {(callData?.to ?? callData?.from) as string}
                </Typography>
              </Stack>
            </Stack>
            {callData?.caseId && (
              <Button
                sx={{ textTransform: 'capitalize' }}
                onClick={handleNavigateToCallCase(callData?.caseId ?? 0)}
                variant="text"
                color="secondary"
              >
                {t('calls.inProgress.caseNumber', { caseId: callData?.caseId })}
              </Button>
            )}
            <Stack>
              <CallDuration />
            </Stack>
            <Stack gap={2} justifyContent="center" direction="row" alignItems="center">
              {!joiningAgent && (
                <Fab onClick={callData?.isJoining ? handleLeaveCall : handleHangUp} color="error" aria-label="hangup">
                  <CallEnd />
                </Fab>
              )}
              <IconButton onClick={handleMute} sx={{ color: (theme) => theme.palette.secondary.main }}>
                {isMuted ? <MicOff /> : <Mic />}
              </IconButton>
              <IconButton
                onClick={handleToggleDtmfDialer}
                sx={{
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  padding: 3,
                  color: (theme) => theme.palette.secondary.main,
                  ...(showDtmfDialer && {
                    backgroundColor: (theme) => theme.palette.secondary.states?.focusVisible,
                  }),
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.secondary.states?.focusVisible,
                  },
                }}
              >
                <Dialpad />
              </IconButton>
              <IconButton onClick={handleMute} sx={{ color: (theme) => theme.palette.secondary.main }}>
                <Pause />
              </IconButton>
              {!callData?.isJoining ? (
                <TransferButton isInactive={showTransferAgentsList} onClick={handleTransferMode(true)} />
              ) : (
                <Fab color="success" aria-label="transfer-call" onClick={handleAcceptCallTransfer}>
                  <PhoneForwarded />
                </Fab>
              )}
            </Stack>
          </Stack>
          {showTransferAgentsList && !showDtmfDialer && (
            <>
              <Divider
                flexItem
                orientation="vertical"
                sx={{
                  margin: (theme) => theme.spacing(0, 3),
                  backgroundColor: (theme) => theme.palette.grey[700],
                }}
              />
              <Box width={WIDTH} display="flex" flexDirection="column" gap={4}>
                <Box display="grid" gap={2}>
                  <Box display="flex" gap={1} alignItems="center">
                    <IconButton size="small" onClick={handleTransferMode(false)}>
                      <ArrowBack sx={{ color: (theme) => theme.palette.secondary.main }} />
                    </IconButton>
                    <Typography textAlign="left" variant="h6">
                      {t('calls.incoming.transfer.title')}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    className="no-drag"
                    size="small"
                    value={search}
                    placeholder={t('calls.incoming.transfer.searchPlaceholder')}
                    InputProps={{
                      sx: {
                        backgroundColor: (theme) => theme.palette.grey[800],
                        color: (theme) => theme.palette.common.white,
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: (theme) => theme.palette.background.paper }} />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Box>

                <Box display="grid" gap={3}>
                  <Typography textAlign="left" color="grey.600" variant="subtitle2">
                    {t('calls.incoming.transfer.freqUsers')}
                  </Typography>

                  <List
                    dense
                    sx={{
                      display: 'grid',
                      padding: 0,
                      gap: 0.5,
                      maxHeight: 200,
                      overflowY: 'auto',
                    }}
                  >
                    {connectedAgents
                      .filter((value) => value.userId !== myProfile?.id)
                      .filter((value) => value.status === AgentStatus.AVAILABLE)
                      .filter((value) =>
                        `${value.firstName?.trim().toLowerCase() ?? ''} ${
                          value.lastName?.trim().toLowerCase() ?? ''
                        }`.includes(search.trim().toLowerCase()),
                      )
                      .map((value) => (
                        <ListItemButton onClick={handleTransfer(value.userId)} sx={{ padding: 1 }} key={value.userId}>
                          <ListItem
                            sx={{
                              padding: 0,
                              '& .MuiListItemSecondaryAction-root': {
                                right: 0,
                              },
                            }}
                            secondaryAction={<PhoneForwarded sx={{ color: (theme) => theme.palette.secondary.main }} />}
                            disablePadding
                          >
                            <ListItemAvatar
                              sx={{
                                minWidth: 32,
                              }}
                            >
                              <Avatar
                                sx={{ width: 24, height: 24 }}
                                alt={`avatar ${value.userId}-${value.firstName}`}
                              />
                            </ListItemAvatar>
                            <ListItemText
                              id={value.userId}
                              primary={`${value.firstName ?? ''} ${value.lastName ?? ''}`}
                            />
                          </ListItem>
                        </ListItemButton>
                      ))}
                  </List>
                </Box>
              </Box>
            </>
          )}
          {joiningAgent && (
            <>
              <Divider
                flexItem
                orientation="vertical"
                sx={{
                  margin: (theme) => theme.spacing(0, 3),
                  backgroundColor: (theme) => theme.palette.grey[700],
                }}
              />
              <Box
                padding={3}
                width={WIDTH}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                gap={4}
              >
                <Stack gap={2} flexDirection={DIRECTION as FlexDirection} alignItems="center" justifyContent="center">
                  <Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, margin: '0 auto' }} />
                  <Stack>
                    <Typography color="primary.contrastText" variant="h6" align="center">
                      {getClientName(joiningAgent.calleeInfo as ClientResponseDto)}
                    </Typography>
                    <Typography color="grey.400" variant="body2" align={fullMode ? 'center' : 'left'}>
                      {hasAgentJoined ? t('calls.incoming.transfer.joined') : t('calls.incoming.transfer.calling')}
                    </Typography>
                  </Stack>
                </Stack>
                {hasAgentJoined ? (
                  <Stack justifyContent="center" alignItems="center" gap={4} sx={{ width: '100%' }}>
                    <CallDuration />
                    <Stack direction="row" spacing={4} alignItems="center" justifyContent="center">
                      <Fab onClick={handleLeaveCall} color="error" aria-label="hangup">
                        <CallEnd />
                      </Fab>
                      <IconButton
                        onClick={handleMute}
                        sx={{ color: (theme) => theme.palette.warning.main, bgcolor: 'transparent' }}
                      >
                        {isMuted ? <MicOff /> : <Mic />}
                      </IconButton>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack justifyContent="flex-end" alignItems="center">
                    <IconButton
                      onClick={handleCancelTransfer}
                      sx={{
                        padding: 2,
                        color: (theme) => theme.palette.common.white,
                        backgroundColor: (theme) => theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.error.dark,
                        },
                      }}
                    >
                      <CallEnd sx={{ width: 24, height: 24 }} />
                    </IconButton>
                  </Stack>
                )}
              </Box>
            </>
          )}
          {showDtmfDialer && !showTransferAgentsList && (
            <>
              <Divider
                flexItem
                orientation="vertical"
                sx={{
                  backgroundColor: (theme) => theme.palette.grey[700],
                }}
              />

              <DtmfDialer />
            </>
          )}
        </Stack>
      </Box>
    </Draggable>
  );
};
